import * as React from 'react'
import { Input, InputProps } from '~/sotano-kit/primitives/input'
import { Switch, SwitchThumb } from '~/sotano-kit/primitives/switch'
import { READMEStepper } from './steps'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useSyncedValue } from '~/hooks/use-synced-value'
import {
  Repository,
  Team,
  VercelDeployButtonConfig,
} from '@basehub/data/replicache/index'
import { clsx } from 'clsx'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { ButtonLink } from '~/sotano-kit/primitives/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/sotano-kit/primitives/select'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { Subtitle } from '../../../settings/_components/public-info/section'
import { invariant } from '@epic-web/invariant'
import { getTemplateDeployUrl } from '../../../../utils'

export const DeployWithVercel = ({
  repo,
  team,
  visitor,
}: {
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  visitor?: boolean
}) => {
  const isMemberOrHigher = useIsMemberOrHigher()
  const teamReplicache = useRepoReplicache().data?.teamQueryData?.replicache
  const config = useSyncedValue<HTMLInputElement, VercelDeployButtonConfig>({
    value: React.useMemo(() => {
      return {
        isEnabled: repo.readme?.vercelDeployButton?.isEnabled ?? false,
        gitRepoUrl:
          (repo.readme?.vercelDeployButton?.gitRepoUrl || repo?.gitRepoUrl) ??
          '',
        demoUrl:
          repo.readme?.vercelDeployButton?.demoUrl ?? repo?.previewUrl ?? '',
        envVars: repo.readme?.vercelDeployButton?.envVars ?? '',
        tokenScope: repo.readme?.vercelDeployButton?.tokenScope ?? 'read',
      }
    }, [
      repo.readme?.vercelDeployButton?.isEnabled,
      repo.readme?.vercelDeployButton?.demoUrl,
      repo.readme?.vercelDeployButton?.gitRepoUrl,
      repo.readme?.vercelDeployButton?.envVars,
      repo.readme?.vercelDeployButton?.tokenScope,
      repo?.gitRepoUrl,
      repo?.previewUrl,
    ]),
    setValue: async (v) => {
      invariant(repo, 'Repo must be defined')
      invariant(teamReplicache, 'teamReplicache must be defined')
      await teamReplicache.mutate.updateVercelDeployREADME({
        repoSlug: repo.slug,
        repoId: repo.id,
        config: v,
      })
      if (v.gitRepoUrl !== undefined) {
        await teamReplicache.mutate.updateRepositoryPublicInfo({
          gitRepoUrl: v.gitRepoUrl,
          repoId: repo.id,
        })
      }
    },
    enabled: !!repo && !!teamReplicache,
    rateLimitType: 'debounce',
    rateLimitMs: 2_000,
  })

  const { deployURL, enabled, errors } = getTemplateDeployUrl({
    repo: {
      ...repo,
      readme: { vercelDeployButton: { ...config.value } },
    },
    team,
  })

  const tokenScopeOptions = [
    { value: 'read', label: 'Read Token' },
    {
      value: 'write',
      label: 'Admin Token',
      disabled: !isMemberOrHigher,
    },
  ]

  if (visitor) {
    // enabled: deploy with vercel was enabled by a repo member + git url is set by a repo member
    if (!enabled || !config.value.isEnabled) return null

    return (
      <div className="flex justify-between items-center bg-dark-bg border-dark-border-solid px-4 py-3 border rounded-lg">
        <div>
          <p className="font-medium text-dark-bright mb-0.5">
            Deploy this website
          </p>
          <Subtitle>
            Feel free to check out{' '}
            <ButtonLink
              href={config.value.gitRepoUrl}
              unstyled
              focusStyled
              className="text-dark-bright hover:underline"
              target="_blank"
              rel="noopener"
            >
              the code
            </ButtonLink>
            {config.value.demoUrl && (
              <>
                {' '}
                and{' '}
                <ButtonLink
                  href={config.value.demoUrl}
                  unstyled
                  focusStyled
                  className="text-dark-bright hover:underline"
                  target="_blank"
                  rel="noopener"
                >
                  the live demo
                </ButtonLink>
              </>
            )}{' '}
            for this template.
          </Subtitle>
        </div>

        <VercelDeployButton deployURL={deployURL} enabled={enabled} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <READMEStepper
        steps={[
          {
            title: null,
            subtitle: null,
            children: (
              <label className="flex items-center gap-2 w-max">
                <span className="font-medium">Is Enabled</span>
                <Switch
                  checked={config.value.isEnabled}
                  onCheckedChange={(v) => {
                    config.onChange((p) => {
                      return { ...p, isEnabled: v }
                    })
                  }}
                >
                  <SwitchThumb />
                </Switch>
              </label>
            ),
          },
          {
            disabled: !config.value.isEnabled,
            title: 'Git Repository',
            subtitle: 'The repository that Vercel will clone and deploy from.',
            children: (
              <READMEInput
                tabIndex={config.value.isEnabled ? undefined : -1}
                error={errors?.gitRepoUrl}
                placeholder="https://github.com/team/repo"
                value={config.value.gitRepoUrl}
                onChange={(e) => {
                  config.onChange((p) => {
                    return { ...p, gitRepoUrl: e.target.value }
                  })
                }}
              />
            ),
          },
          {
            disabled: !config.value.isEnabled,
            title: 'Token Scope',
            subtitle: `If this template requires more than just the read token's scope.`,
            children: (
              <READMESelectInput
                tabIndex={config.value.isEnabled ? undefined : -1}
                error={errors?.tokenScope}
                value={config.value.tokenScope || 'read'}
                options={tokenScopeOptions}
                onValueChange={(value) => {
                  config.onChange((p) => {
                    return {
                      ...p,
                      tokenScope: value as 'read' | 'write',
                    }
                  })
                }}
              />
            ),
          },
          {
            disabled: !config.value.isEnabled,
            title: 'Demo Website URL (optional)',
            subtitle:
              'Vercel will extract a screenshot and metadata from this website for visitors to quickly see.',
            children: (
              <READMEInput
                error={errors?.demoUrl}
                tabIndex={config.value.isEnabled ? undefined : -1}
                placeholder="https://acme-site.vercel.app (optional)"
                value={config.value.demoUrl ?? ''}
                onChange={(e) => {
                  config.onChange((p) => {
                    return {
                      ...p,
                      demoUrl: e.target.value,
                    }
                  })
                }}
              />
            ),
          },
          {
            disabled: !config.value.isEnabled,
            title: 'Additional Environment Variables (optional)',
            subtitle:
              'Specify other, non BaseHub related environment variables. Comma separated.',
            children: (
              <READMEInput
                error={errors?.envVars}
                tabIndex={config.value.isEnabled ? undefined : -1}
                placeholder="(optional)"
                value={config.value.envVars ?? ''}
                onChange={(e) => {
                  config.onChange((p) => {
                    return {
                      ...p,
                      envVars: e.target.value,
                    }
                  })
                }}
              />
            ),
          },
          {
            disabled: !config.value.isEnabled,
            title: 'Try it out',
            subtitle: 'Try the Deploy Button out.',
            children: (
              <VercelDeployButton deployURL={deployURL} enabled={enabled} />
            ),
          },
        ]}
      />
    </div>
  )
}

const READMEInput = ({
  error,
  ...inputProps
}: InputProps & {
  error: string | null | undefined
}) => {
  const [hasErrorOnMount, setHasErrorOnMount] = React.useState(false)
  const [touched, setTouched] = React.useState(false)

  const mounted = React.useRef(false)
  React.useEffect(() => {
    if (mounted.current) return
    mounted.current = true
    if (error) {
      setHasErrorOnMount(true)
    }
  }, [error])

  return (
    <>
      <Input
        className="max-w-[600px]"
        {...inputProps}
        onBlur={() => setTouched(true)}
      />
      {error && (hasErrorOnMount || touched) && (
        <div className="text-red text-sm mt-2 flex gap-2 items-start max-w-[600px] text-pretty">
          <span className="pt-[3px]">
            <ExclamationTriangleIcon />
          </span>{' '}
          {error}
        </div>
      )}
    </>
  )
}

const READMESelectInput = ({
  error,
  value,
  onValueChange,
  tabIndex,
  options,
}: {
  value: string | undefined
  onValueChange: (v: string) => void
  error: string | null | undefined
  tabIndex?: number
  options: Array<{ label: string; value: string; disabled?: boolean }>
}) => {
  const [hasErrorOnMount, setHasErrorOnMount] = React.useState(false)
  const [touched, setTouched] = React.useState(false)

  const mounted = React.useRef(false)
  React.useEffect(() => {
    if (mounted.current) return
    mounted.current = true
    if (error) {
      setHasErrorOnMount(true)
    }
  }, [error])

  return (
    <>
      <Select value={value} onValueChange={onValueChange}>
        <SelectTrigger
          aria-label="token scope"
          iconRight
          onBlur={() => {
            setTouched(true)
          }}
          tabIndex={tabIndex}
          className="w-full max-w-[600px] h-[36px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => {
            return (
              <SelectItem
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.value}
              </SelectItem>
            )
          })}
        </SelectContent>
      </Select>
      {error && (hasErrorOnMount || touched) && (
        <div className="text-red text-sm mt-2 flex gap-2 items-start max-w-[600px] text-pretty">
          <span className="pt-[3px]">
            <ExclamationTriangleIcon />
          </span>{' '}
          {error}
        </div>
      )}
    </>
  )
}

const VercelDeployButton = ({
  deployURL,
  enabled,
}: {
  deployURL: string | undefined
  enabled?: boolean
}) => {
  return (
    <div
      className={clsx(
        'flex select-none px-0.5 py-1',
        !enabled && 'opacity-50 pointer-events-none cursor-not-allowed'
      )}
    >
      <ButtonLink
        href={deployURL ?? '#'}
        unstyled
        tabIndex={enabled ? undefined : -1}
        target="_blank"
        rel="noopener"
        className={enabled ? 'undefined' : 'cursor-not-allowed'}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          style={{ height: 32 }}
          src="https://vercel.com/button"
          alt="Deploy with Vercel"
        />
      </ButtonLink>
    </div>
  )
}
